import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, ShieldCheck, Monitor, Smartphone } from 'lucide-react';
import CaptchaWidget from '../components/CaptchaWidget';
import LanguageSelector from '../components/LanguageSelector';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  // 2FA state
  const [show2FA, setShow2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [emailHint, setEmailHint] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [verifying, setVerifying] = useState(false);
  const [smsSent, setSmsSent] = useState(false);
  const [phoneHint, setPhoneHint] = useState('');
  const [resendingSms, setResendingSms] = useState(false);
  const inputRefs = useRef([]);

  const { login, verify2FA } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    if (show2FA && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [show2FA]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, password, captchaToken);
      if (result.requires_2fa) {
        setTempToken(result.temp_token);
        setDeviceName(result.device_name);
        setEmailHint(result.email_hint);
        setSmsSent(result.sms_sent || false);
        setPhoneHint(result.phone_hint || '');
        setShow2FA(true);
        toast.info(t('Código de verificación enviado a tu email'));
      } else {
        toast.success(t('Sesión iniciada correctamente'));
        navigate('/dashboard');
      }
    } catch (error) {
      const detail = error.response?.data?.detail || t('Error al iniciar sesión');
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length > 0) {
      const newCode = [...code];
      for (let i = 0; i < 6; i++) {
        newCode[i] = pasted[i] || '';
      }
      setCode(newCode);
      const focusIdx = Math.min(pasted.length, 5);
      inputRefs.current[focusIdx]?.focus();
    }
  };

  const handleVerify2FA = async () => {
    const fullCode = code.join('');
    if (fullCode.length !== 6) return toast.error(t('Introduce el código de 6 dígitos'));
    setVerifying(true);
    try {
      await verify2FA(tempToken, fullCode);
      toast.success(t('Verificación correcta'));
      navigate('/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail || t('Código incorrecto');
      toast.error(detail);
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  };

  const handleBack = () => {
    setShow2FA(false);
    setTempToken('');
    setCode(['', '', '', '', '', '']);
  };

  // 2FA Verification Screen
  if (show2FA) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-8">
        <div className="noise-overlay" />
        <div className="w-full max-w-md relative z-10">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShieldCheck className="w-8 h-8 text-primary" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-white mb-2">{t('VERIFICACIÓN')}</h1>
            <p className="text-zinc-400">{t('Dispositivo nuevo detectado')}</p>
          </div>

          <div className="bg-surface border border-white/10 rounded-md p-6 mb-6">
            <div className="flex items-center gap-3 mb-4 p-3 bg-surface-highlight rounded-md">
              <Monitor className="w-5 h-5 text-primary flex-shrink-0" />
              <div>
                <p className="text-white text-sm font-medium">{deviceName}</p>
                <p className="text-zinc-500 text-xs">{t('Dispositivo no reconocido')}</p>
              </div>
            </div>

            <p className="text-zinc-400 text-sm mb-6">
              {t('Hemos enviado un código de 6 dígitos a')} <span className="text-white font-medium">{emailHint}</span>
              {smsSent && phoneHint && <span> {t('y por SMS a')} <span className="text-white font-medium">{phoneHint}</span></span>}
              . {t('Introdúcelo para continuar.')}
            </p>

            <div className="flex gap-2 justify-center mb-6" onPaste={handleCodePaste}>
              {code.map((digit, i) => (
                <input
                  key={i}
                  ref={el => inputRefs.current[i] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={e => handleCodeChange(i, e.target.value)}
                  onKeyDown={e => handleCodeKeyDown(i, e)}
                  className="w-12 h-14 text-center text-2xl font-bold bg-surface-highlight border border-white/10 rounded-md text-white focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
                  data-testid={`2fa-code-${i}`}
                />
              ))}
            </div>

            <Button
              onClick={handleVerify2FA}
              disabled={verifying || code.join('').length !== 6}
              className="w-full bg-primary hover:bg-primary/90 text-white font-heading uppercase tracking-wider h-12"
              data-testid="verify-2fa-btn"
            >
              {verifying ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  {t('Verificando...')}
                </>
              ) : (
                t('Verificar')
              )}
            </Button>

            <p className="text-zinc-500 text-xs text-center mt-4">
              {t('El código caduca en 10 minutos. Máximo 3 intentos.')}
            </p>

            {phoneHint && (
              <button
                onClick={async () => {
                  setResendingSms(true);
                  try {
                    await axios.post(`${API}/auth/resend-2fa-sms`, { temp_token: tempToken });
                    toast.success(t('Código reenviado por SMS'));
                    setSmsSent(true);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || t('Error al reenviar SMS'));
                  } finally { setResendingSms(false); }
                }}
                disabled={resendingSms}
                className="w-full mt-3 flex items-center justify-center gap-2 text-zinc-400 hover:text-white text-sm py-2 border border-white/5 rounded-md hover:border-white/10 transition-colors"
                data-testid="resend-sms-btn"
              >
                {resendingSms ? <Loader2 className="w-3 h-3 animate-spin" /> : <Smartphone className="w-3 h-3" />}
                {t('¿No tienes acceso al email? Reenviar por SMS')} ({phoneHint})
              </button>
            )}
          </div>

          <div className="text-center">
            <button onClick={handleBack} className="text-zinc-500 hover:text-white text-sm">
              ← {t('Volver al inicio de sesión')}
            </button>
          </div>
        </div>
        <Toaster position="top-right" richColors />
      </div>
    );
  }

  // Normal Login Screen
  return (
    <div className="min-h-screen bg-background flex">
      <div className="noise-overlay" />
      
      {/* Left Side - Image */}
      <div 
        className="hidden lg:block lg:w-1/2 relative"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1637300417161-bff1e04ea9f6?crop=entropy&cs=srgb&fm=jpg&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 to-black/50" />
        <div className="absolute inset-0 flex flex-col justify-center p-12">
          <div className="flex items-center gap-3 mb-8">
            <img src="/logo-ar.png" alt="AR Performance" className="w-12 h-12 object-contain" />
            <div>
              <h1 className="font-heading text-2xl font-bold text-white tracking-tight">
                AR PERFORMANCE
              </h1>
              <p className="text-sm text-zinc-400 uppercase tracking-wider">FileService</p>
            </div>
          </div>
          <h2 className="font-heading text-4xl font-bold text-white mb-4">
            {t('PORTAL DE')}<br />
            <span className="text-primary">{t('PROFESIONALES')}</span>
          </h2>
          <p className="text-zinc-400 max-w-md">
            {t('Accede a tu cuenta para solicitar archivos, gestionar tus créditos y descargar tus modificaciones.')}
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 relative">
        <div className="absolute top-4 right-4"><LanguageSelector /></div>
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <img src="/logo-ar.png" alt="AR Performance" className="w-10 h-10 object-contain" />
            <span className="font-heading text-xl font-bold text-white">AR PERFORMANCE</span>
          </div>

          <div className="text-center mb-8">
            <h1 className="font-heading text-3xl font-bold text-white mb-2">{t('INICIAR SESIÓN')}</h1>
            <p className="text-zinc-400">{t('Introduce tus credenciales para acceder')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-300">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                required
                className="bg-surface-highlight border-white/10 text-white placeholder:text-zinc-600 h-12"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-300">{t('Contraseña')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="bg-surface-highlight border-white/10 text-white placeholder:text-zinc-600 h-12 pr-12"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <CaptchaWidget onVerify={setCaptchaToken} />

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-primary hover:bg-primary/90 text-white font-heading uppercase tracking-wider h-12"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  {t('Iniciando...')}
                </>
              ) : (
                t('Iniciar Sesión')
              )}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <Link to="/forgot-password" className="text-zinc-500 hover:text-primary text-sm" data-testid="forgot-password-link">
              {t('¿Olvidaste tu contraseña?')}
            </Link>
          </div>

          <div className="mt-4 text-center">
            <p className="text-zinc-400">
              {t('¿No tienes cuenta?')}{' '}
              <Link to="/register" className="text-primary hover:underline" data-testid="register-link">
                {t('Regístrate aquí')}
              </Link>
            </p>
          </div>

          <div className="mt-8 text-center space-y-3">
            <Link to="/" className="text-zinc-500 hover:text-white text-sm block">
              ← {t('Volver al inicio')}
            </Link>
            <div className="flex items-center justify-center gap-1.5 text-zinc-600 text-xs">
              <svg className="w-3.5 h-3.5" viewBox="0 0 64 64" fill="currentColor"><path d="M44.5 28.4l-3.2-10.8c-.1-.4-.5-.7-.9-.5l-8.4 3.1-2.1-.1-1.9-3.2c-.2-.3-.6-.4-.9-.3l-6.3 2.8c-.2.1-.3.2-.4.4l-7.3 20c-.1.3 0 .6.2.8l.8.6c.2.1.4.2.6.1L32 35.1l12.2-5.5c.3-.1.4-.4.3-.7zm-14-5.8l2.1.1c.2 0 .3.1.4.2l1.1 1.9-5 1.8 1.4-4zM32 48c-8.8 0-16-7.2-16-16S23.2 16 32 16s16 7.2 16 16-7.2 16-16 16z"/></svg>
              <span>Site protected by Cloudflare</span>
            </div>
            <div className="flex items-center justify-center gap-3 text-zinc-600 text-xs">
              <Link to="/legal/aviso-legal" className="hover:text-zinc-400">{t('Aviso Legal')}</Link>
              <span>·</span>
              <Link to="/legal/privacidad" className="hover:text-zinc-400">{t('Privacidad')}</Link>
              <span>·</span>
              <Link to="/legal/cookies" className="hover:text-zinc-400">Cookies</Link>
            </div>
          </div>
        </div>
      </div>

      <Toaster position="top-right" richColors />
    </div>
  );
};

export default LoginPage;
